import { frontOfficeInstance } from '@/shared/api';

export const PUBLIC_API_SCOPES = [
  'channels:read',
  'contacts:read',
  'chats:read',
  'messages:read',
  'messages:write',
  'webhooks:write',
  '*',
] as const;

export const PUBLIC_API_WEBHOOK_EVENTS = [
  'message.created',
  'message.updated',
  'message.deleted',
  'message.reaction.updated',
] as const;

export type PublicApiScope = (typeof PUBLIC_API_SCOPES)[number];
export type PublicApiWebhookEvent = (typeof PUBLIC_API_WEBHOOK_EVENTS)[number];

export interface PublicApiKey {
  id: string;
  account_id: string;
  name: string;
  prefix: string;
  scopes: PublicApiScope[];
  status: string;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
  token?: string;
  revoked_key_id?: string;
}

export interface PublicApiChannel {
  id: string;
  name?: string;
  protocol: string;
  status: string;
  is_active: boolean;
  phone_number: string;
}

export interface PublicApiWebhook {
  id: string;
  account_id: string;
  channel_id: string;
  url: string;
  events: PublicApiWebhookEvent[];
  status: string;
  created_at: string;
  updated_at: string;
  revoked_at: string | null;
  signing_secret?: string;
}

interface ListResponse<T> {
  data: { items: T[] };
}

interface ItemResponse<T> {
  data: T;
}

export const getPublicApiKeys = async (): Promise<PublicApiKey[]> => {
  const { data } =
    await frontOfficeInstance.get<ListResponse<PublicApiKey>>(
      '/api/public-api/api-keys'
    );
  return data.data.items;
};

export const createPublicApiKey = async (input: {
  name: string;
  scopes: PublicApiScope[];
}): Promise<PublicApiKey> => {
  const { data } =
    await frontOfficeInstance.post<ItemResponse<PublicApiKey>>(
      '/api/public-api/api-keys',
      input
    );
  return data.data;
};

export const revokePublicApiKey = async (
  keyId: string
): Promise<PublicApiKey> => {
  const { data } =
    await frontOfficeInstance.delete<ItemResponse<PublicApiKey>>(
      `/api/public-api/api-keys/${keyId}`
    );
  return data.data;
};

export const rotatePublicApiKey = async (
  keyId: string
): Promise<PublicApiKey> => {
  const { data } =
    await frontOfficeInstance.post<ItemResponse<PublicApiKey>>(
      `/api/public-api/api-keys/${keyId}/rotate`
    );
  return data.data;
};

export const getPublicApiChannels = async (): Promise<PublicApiChannel[]> => {
  const { data } =
    await frontOfficeInstance.get<ListResponse<PublicApiChannel>>(
      '/api/public-api/channels'
    );
  return data.data.items;
};

export const getPublicApiWebhooks = async (
  channelId: string
): Promise<PublicApiWebhook[]> => {
  const { data } =
    await frontOfficeInstance.get<ListResponse<PublicApiWebhook>>(
      `/api/public-api/channels/${channelId}/webhooks`
    );
  return data.data.items;
};

export const upsertPublicApiWebhook = async (input: {
  channelId: string;
  url: string;
  events: PublicApiWebhookEvent[];
}): Promise<PublicApiWebhook> => {
  const { data } =
    await frontOfficeInstance.put<ItemResponse<PublicApiWebhook>>(
      `/api/public-api/channels/${input.channelId}/webhooks`,
      { url: input.url, events: input.events }
    );
  return data.data;
};

export const deletePublicApiWebhook = async (input: {
  channelId: string;
  webhookId: string;
}): Promise<PublicApiWebhook> => {
  const { data } =
    await frontOfficeInstance.delete<ItemResponse<PublicApiWebhook>>(
      `/api/public-api/channels/${input.channelId}/webhooks/${input.webhookId}`
    );
  return data.data;
};
